import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiCpu,
  FiDownload,
  FiExternalLink,
  FiEye,
  FiGlobe,
  FiHash,
  FiLayers,
  FiPackage,
  FiShield,
  FiUpload,
  FiUser,
  FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { usePersistedOfflineSignerAddress } from '../../hooks/usePersistedOfflineSignerAddress';
import { getStandardContractTemplate, resolveContractArtifact } from '../../blockchain/standardContractTemplates';
import { resolveContractTemplateIdForAssetClass } from '../../blockchain/resolveMintArtifactForAssetClass';
import {
  formatEnumLabel,
  formatSectorLabel,
  normalizeAssetClassTokenStandard
} from '../../api/smartTokenizerMappers';
import {
  buildSignerOutputTemplate,
  copyTextToClipboard,
  downloadJsonFile
} from '../../blockchain/offlineSigning';
import {
  bulkRegistrationMembers,
  delay,
  formatMintRequestStatus,
  formatPreparedPayload,
  latestMintRequestFromMemberHistory,
  MINTED_ASSET_STATUSES,
  memberDocumentsCount,
  mintRequestAllowsRebroadcast,
  normalizeBulkMintPrepareResponse,
  parseSignedTransactionInput,
  preferredMintRequestForDisplay
} from '../../utils/bulkRegistrationMintHelpers';

/** Matches Assets Registry tokenization modal (`TokenizeWizardModalOverlay`). */
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1.5rem;
`;

/** Matches `TokenizeWizardModalContent` in TokenizerMyAssets. */
const ModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 960px;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const WizardContainer = styled.div`
  padding: 2rem;
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitleBlock = styled.div`
  min-width: 0;
`;

const WizardTitle = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const WizardSubtitle = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #a0a0a0;
  line-height: 1.55;
  max-width: 36rem;
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ProgressBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 1.75rem;
  padding: 0.75rem 0.5rem 1rem;
  gap: 0;
  row-gap: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 0 1 auto;
  min-width: 5.5rem;
  max-width: 8rem;
  padding: 0 0.35rem;

  .step-number {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.82rem;
    flex-shrink: 0;
    background: ${(p) =>
      p.$active ? '#4facfe' : p.$completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
    color: ${(p) => (p.$active || p.$completed ? '#fff' : '#a0a0a0')};
    border: 2px solid
      ${(p) => (p.$active ? '#4facfe' : p.$completed ? '#00ff88' : 'rgba(255, 255, 255, 0.2)')};
    box-shadow: ${(p) => (p.$active ? '0 0 14px rgba(79, 172, 254, 0.28)' : 'none')};
  }

  .step-label {
    color: ${(p) => (p.$active ? '#4facfe' : p.$completed ? '#00ff88' : '#a0a0a0')};
    font-weight: ${(p) => (p.$active ? '600' : '400')};
    font-size: 0.78rem;
    line-height: 1.3;
    text-align: center;
    width: 100%;
  }
`;

const StepConnector = styled.div`
  width: clamp(0.65rem, 2vw, 1.75rem);
  min-width: 0.5rem;
  height: 2px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: calc(2.25rem / 2 - 1px);
  background: ${(p) =>
    p.$completed
      ? 'linear-gradient(90deg, #00ff88 0%, rgba(79, 172, 254, 0.85) 100%)'
      : 'rgba(255, 255, 255, 0.15)'};
`;

const StepContent = styled(motion.div)`
  min-height: 280px;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.1);
    }
    &::placeholder {
      color: #a0a0a0;
    }
  }

  textarea {
    min-height: 100px;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.8rem;
    resize: vertical;
  }

  select option {
    background: #1a1a3e;
    color: #fff;
  }

  .helper {
    color: #a0a0a0;
    font-size: 0.8rem;
    margin-top: 0.35rem;
    line-height: 1.45;
  }
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;

  .review-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .review-icon {
    font-size: 2rem;
    color: #4facfe;
  }

  .review-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
  }

  .review-meta {
    color: #a0a0a0;
    font-size: 0.9rem;
    line-height: 1.55;
    code {
      color: #e2e8f0;
      font-size: 0.8rem;
    }
  }
`;

const MemberTableWrap = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  margin-top: 1rem;
`;

const MemberTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;

  th,
  td {
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  th {
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.38);
  }

  tbody tr {
    transition: background 0.15s;
  }

  tbody tr:hover {
    background: rgba(79, 172, 254, 0.07);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  color: #fff;

  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }

  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
  }
`;

const FlowSteps = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;

    &:focus {
      outline: none;
      border-color: #4facfe;
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.1);
    }
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 1.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;

  &.primary {
    background: #4facfe;
    color: #fff;
    &:hover:not(:disabled) {
      background: #3d8bfe;
      transform: translateY(-1px);
    }
    &:disabled {
      background: #2a2a3e;
      color: #a0a0a0;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
    }
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;

const SignStepBanner = styled.div`
  background: rgba(79, 172, 254, 0.1);
  border: 1px solid rgba(79, 172, 254, 0.25);
  border-radius: 12px;
  padding: 0.85rem 1.1rem;
  margin-bottom: 1.25rem;
  color: #bae6fd;
  font-size: 0.9rem;
  line-height: 1.5;

  strong {
    color: #e0f2fe;
  }
`;

const UnsignedMintGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.85rem;
  margin-top: 0.25rem;
`;

const UnsignedMintCard = styled.div`
  background: rgba(15, 15, 35, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;

  &.current {
    border-color: rgba(79, 172, 254, 0.55);
    box-shadow: 0 0 0 1px rgba(79, 172, 254, 0.22);
  }
`;

const UnsignedMintCardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const UnsignedMintStepPill = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
`;

const UnsignedMintCurrentBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: rgba(79, 172, 254, 0.2);
  color: #bae6fd;
`;

const UnsignedMintAssetTitle = styled.div`
  font-size: 0.82rem;
  color: #cbd5e1;
  line-height: 1.35;
  word-break: break-word;
`;

const UnsignedMintTokenRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const UnsignedMintTokenLabel = styled.span`
  font-size: 0.72rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const UnsignedMintTokenId = styled.span`
  font-family: Consolas, Monaco, monospace;
  font-size: 0.95rem;
  color: #f1f5f9;
  font-weight: 600;
`;

/** Stacks above main wizard overlay (z-index 1100). */
const AssetDetailOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1250;
  padding: 1.5rem;
`;

const AssetDetailCard = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 560px;
  width: 100%;
  max-height: min(88vh, 720px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);

  &::-webkit-scrollbar {
    width: 6px;
  }
`;

const AssetDetailHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.15);
`;

const AssetDetailHeadText = styled.div`
  min-width: 0;
`;

const AssetDetailTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.02em;

  svg {
    color: #4facfe;
    flex-shrink: 0;
  }
`;

const AssetDetailHeadSub = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: #94a3b8;
  font-family: ui-monospace, monospace;
`;

const AssetDetailBody = styled.div`
  padding: 0 1.5rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  font-size: 0.88rem;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.35);
    border-radius: 3px;
  }
`;

const AssetDetailHero = styled.div`
  margin: 1.25rem 0 1rem;
  padding: 1.1rem 1.2rem;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.12) 0%, rgba(0, 242, 254, 0.06) 100%);
  border: 1px solid rgba(79, 172, 254, 0.22);
`;

const AssetDetailHeroTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #f8fafc;
  line-height: 1.35;
  margin-bottom: 0.65rem;
`;

const DetailStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
  ${(p) => {
    switch (p.$tone) {
      case 'success':
        return `
          background: rgba(0, 255, 136, 0.12);
          color: #4ade80;
          border-color: rgba(74, 222, 128, 0.35);
        `;
      case 'danger':
        return `
          background: rgba(248, 113, 113, 0.12);
          color: #fca5a5;
          border-color: rgba(248, 113, 113, 0.35);
        `;
      case 'warning':
        return `
          background: rgba(251, 191, 36, 0.12);
          color: #fcd34d;
          border-color: rgba(251, 191, 36, 0.35);
        `;
      case 'progress':
        return `
          background: rgba(79, 172, 254, 0.15);
          color: #7dd3fc;
          border-color: rgba(125, 211, 252, 0.4);
        `;
      default:
        return `
          background: rgba(148, 163, 184, 0.12);
          color: #cbd5e1;
          border-color: rgba(148, 163, 184, 0.25);
        `;
    }
  }}
`;

const DetailInfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: rgba(79, 172, 254, 0.08);
  border: 1px solid rgba(79, 172, 254, 0.22);
  color: #bae6fd;
  font-size: 0.8rem;
  line-height: 1.5;
  margin-bottom: 1.1rem;
`;

const DetailSection = styled.section`
  margin-bottom: 1.15rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const DetailSectionTitle = styled.h4`
  margin: 0 0 0.65rem;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.45rem;

  svg {
    color: #4facfe;
    opacity: 0.9;
  }
`;

const DetailFieldList = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const DetailFieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, 36%) 1fr;
  gap: 0.5rem 1rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  align-items: start;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailFieldKey = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  padding-top: 0.12rem;
`;

const DetailFieldValueWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  min-width: 0;
`;

const DetailFieldValue = styled.div`
  color: #f1f5f9;
  font-size: 0.86rem;
  line-height: 1.45;
  word-break: break-word;
  flex: 1;
`;

const DetailFieldMono = styled(DetailFieldValue)`
  font-family: ui-monospace, Consolas, monospace;
  font-size: 0.78rem;
  color: #e2e8f0;
`;

const DetailInlineCopy = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s, border-color 0.15s;

  &:hover {
    color: #4facfe;
    background: rgba(79, 172, 254, 0.12);
    border-color: rgba(79, 172, 254, 0.35);
  }
`;

const DetailStatHighlight = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.9rem;
  color: #4facfe;
  background: rgba(79, 172, 254, 0.12);
  border: 1px solid rgba(79, 172, 254, 0.25);
`;

const DetailEmpty = styled.span`
  color: #64748b;
  font-style: italic;
  font-size: 0.84rem;
`;

const RowIconBtn = styled.button.attrs({ type: 'button' })`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: rgba(79, 172, 254, 0.12);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(79, 172, 254, 0.22);
    color: #e0f2fe;
  }
`;

const ContractAddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0 0 0.65rem;
`;

const ContractAddressMono = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, Consolas, monospace;
  font-size: 0.82rem;
  color: #e2e8f0;
  word-break: break-all;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ContractExplorerIconBtn = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  margin-top: 0.35rem;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: rgba(79, 172, 254, 0.12);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.22);
    color: #e0f2fe;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

function humanizeStatusLabel(raw) {
  if (raw == null || raw === '') return 'Unknown';
  const s = String(raw).trim();
  return s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function statusToneFromRaw(status) {
  const canon = String(status ?? '')
    .replace(/\s+/g, '')
    .replace(/_/g, '')
    .toLowerCase();
  if (canon.includes('approvedfortokenization') || canon === 'minted' || canon.includes('metadatapinned'))
    return 'success';
  if (canon.includes('rejected')) return 'danger';
  if (canon.includes('draft')) return 'neutral';
  if (canon.includes('legal') || canon.includes('submitted') || canon.includes('review') || canon.includes('pending'))
    return 'progress';
  if (canon.includes('mint')) return 'progress';
  return 'neutral';
}

function AssetDetailFields({ asset, loading }) {
  const id = pick(asset, 'id', 'Id');
  const assetNum = pick(asset, 'assetNumber', 'AssetNumber');
  const title = pick(asset, 'title', 'Title', 'name', 'Name');
  const statusRaw = pick(asset, 'status', 'Status');
  const sector = pick(asset, 'sector', 'Sector');
  const country = pick(asset, 'country', 'Country');
  const description = pick(asset, 'description', 'Description');
  const owner = pick(
    asset,
    'currentOwnerIdentifier',
    'CurrentOwnerIdentifier',
    'ownerAddress',
    'OwnerAddress'
  );
  const ipfs = pick(asset, 'metadataIpfsCid', 'MetadataIpfsCid');
  const docs = memberDocumentsCount(asset);
  const tok = pick(asset, 'tokenizationId', 'TokenizationId');

  const copy = (text, label) => {
    const t = text != null && String(text).trim() !== '' ? String(text) : '';
    if (!t) return;
    copyTextToClipboard(t);
    toast.success(`${label} copied`);
  };

  const row = (key, label, valueNode) => (
    <DetailFieldRow key={key}>
      <DetailFieldKey>{label}</DetailFieldKey>
      <DetailFieldValueWrap>{valueNode}</DetailFieldValueWrap>
    </DetailFieldRow>
  );

  return (
    <>
      <AssetDetailHero>
        <AssetDetailHeroTitle>{title ? String(title) : 'Untitled asset'}</AssetDetailHeroTitle>
        <DetailStatusBadge $tone={statusToneFromRaw(statusRaw)}>
          {statusRaw != null && String(statusRaw).trim() !== ''
            ? humanizeStatusLabel(statusRaw)
            : 'Unknown'}
        </DetailStatusBadge>
      </AssetDetailHero>

      <DetailInfoBanner>
        {loading
          ? 'Loading the latest record from the API…'
          : 'Synced from the server when available; otherwise showing the batch snapshot from this wizard.'}
      </DetailInfoBanner>

      <DetailSection>
        <DetailSectionTitle>
          <FiHash aria-hidden />
          Identifiers
        </DetailSectionTitle>
        <DetailFieldList>
          {row(
            'id',
            'Asset ID',
            <>
              <DetailFieldMono>{id ? String(id) : <DetailEmpty>—</DetailEmpty>}</DetailFieldMono>
              {id ? (
                <DetailInlineCopy type="button" title="Copy ID" onClick={() => copy(id, 'ID')}>
                  <FiCopy aria-hidden />
                </DetailInlineCopy>
              ) : null}
            </>,
          )}
          {row(
            'num',
            'Asset #',
            <>
              <DetailFieldValue>{assetNum ? String(assetNum) : <DetailEmpty>—</DetailEmpty>}</DetailFieldValue>
              {assetNum ? (
                <DetailInlineCopy type="button" title="Copy asset number" onClick={() => copy(assetNum, 'Asset number')}>
                  <FiCopy aria-hidden />
                </DetailInlineCopy>
              ) : null}
            </>
          )}
        </DetailFieldList>
      </DetailSection>

      <DetailSection>
        <DetailSectionTitle>
          <FiGlobe aria-hidden />
          Classification
        </DetailSectionTitle>
        <DetailFieldList>
          {row(
            'sector',
            'Sector',
            <DetailFieldValue>{formatSectorLabel(sector)}</DetailFieldValue>
          )}
          {row(
            'country',
            'Country / region',
            <DetailFieldValue>{country != null && country !== '' ? String(country) : <DetailEmpty>Not set</DetailEmpty>}</DetailFieldValue>
          )}
          {row(
            'desc',
            'Description',
            <DetailFieldValue>
              {description != null && String(description).trim() !== '' ? String(description) : <DetailEmpty>—</DetailEmpty>}
            </DetailFieldValue>
          )}
        </DetailFieldList>
      </DetailSection>

      <DetailSection>
        <DetailSectionTitle>
          <FiUser aria-hidden />
          Custody &amp; chain
        </DetailSectionTitle>
        <DetailFieldList>
          {row(
            'owner',
            'Owner / recipient',
            <>
              <DetailFieldMono>
                {owner != null && String(owner).trim() !== '' ? String(owner) : <DetailEmpty>—</DetailEmpty>}
              </DetailFieldMono>
              {owner != null && String(owner).trim() !== '' ? (
                <DetailInlineCopy type="button" title="Copy address" onClick={() => copy(owner, 'Owner / recipient')}>
                  <FiCopy aria-hidden />
                </DetailInlineCopy>
              ) : null}
            </>
          )}
          {row(
            'ipfs',
            'Metadata (IPFS)',
            <>
              <DetailFieldMono>
                {ipfs != null && String(ipfs).trim() !== '' ? String(ipfs) : <DetailEmpty>No CID yet</DetailEmpty>}
              </DetailFieldMono>
              {ipfs != null && String(ipfs).trim() !== '' ? (
                <DetailInlineCopy type="button" title="Copy CID" onClick={() => copy(ipfs, 'IPFS CID')}>
                  <FiCopy aria-hidden />
                </DetailInlineCopy>
              ) : null}
            </>
          )}
          {row(
            'docs',
            'Documents',
            <DetailFieldValue>
              <DetailStatHighlight>{docs}</DetailStatHighlight>
              <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>
                file{docs === 1 ? '' : 's'} on record
              </span>
            </DetailFieldValue>
          )}
          {row(
            'tok',
            'Tokenization ID',
            <DetailFieldMono>
              {tok != null && String(tok).trim() !== '' ? String(tok) : <DetailEmpty>Not tokenized</DetailEmpty>}
            </DetailFieldMono>
          )}
        </DetailFieldList>
      </DetailSection>
    </>
  );
}

function pickPreparedResolvedTokenId(prepared) {
  if (!prepared || typeof prepared !== 'object') return '';
  const v = prepared.resolvedTokenId ?? prepared.ResolvedTokenId;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

function slugForMintFilename(part) {
  return String(part ?? '')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 36);
}

function shortenTxHash(hash) {
  const h = String(hash ?? '').trim();
  if (!h) return '—';
  if (h.length <= 18) return h;
  return `${h.slice(0, 10)}…${h.slice(-8)}`;
}

function BulkTokenizeWizardModal({ open, bulkRegistration, assetClass, onClose, onCompleted }) {
  const { currentIssuer, session } = useTokenIssuer();
  const { addressExplorerUrl, explorerConfigured, transactionExplorerUrl } = useClientConfig();
  const offlineUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const [signerAddress, setSignerAddress] = usePersistedOfflineSignerAddress(offlineUserId);
  const {
    prepareBulkMintByRegistration,
    submitMintToken,
    getMintedTokenByAssetId,
    getAssetById,
    loadAssets,
    listBulkMemberMintHistory,
    rebroadcastMintRequest
  } = useTokenization();

  const [phase, setPhase] = useState('review');
  const [ownerWallet, setOwnerWallet] = useState('');
  const [preparedMint, setPreparedMint] = useState(null);
  const [signedMintInput, setSignedMintInput] = useState('');
  const [bulkMintLineItems, setBulkMintLineItems] = useState([]);
  const [bulkMintCursor, setBulkMintCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const [memberAssetDetail, setMemberAssetDetail] = useState(null);
  const [bulkMemberMintHistory, setBulkMemberMintHistory] = useState([]);
  const [bulkMintHistoryLoading, setBulkMintHistoryLoading] = useState(false);
  /** Hide on-chain attempts table until the user starts a broadcast from this step. */
  const [signingBroadcastStarted, setSigningBroadcastStarted] = useState(false);
  /** Per-asset mint row from last submit response; fills rows before server history refetch completes. */
  const [optimisticMintByAssetId, setOptimisticMintByAssetId] = useState({});

  const bulkId = bulkRegistration ? pick(bulkRegistration, 'id', 'Id') : null;
  const members = useMemo(() => (bulkRegistration ? bulkRegistrationMembers(bulkRegistration) : []), [bulkRegistration]);

  const membersSortedByTitle = useMemo(() => {
    return [...members].sort((a, b) => {
      const ta = String(pick(a, 'title', 'Title') || '').trim();
      const tb = String(pick(b, 'title', 'Title') || '').trim();
      const byTitle = ta.localeCompare(tb, undefined, { numeric: true, sensitivity: 'base' });
      if (byTitle !== 0) return byTitle;
      const na = String(pick(a, 'assetNumber', 'AssetNumber') || pick(a, 'id', 'Id') || '');
      const nb = String(pick(b, 'assetNumber', 'AssetNumber') || pick(b, 'id', 'Id') || '');
      return na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [members]);

  const memberTitleByAssetId = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      const id = pick(m, 'id', 'Id');
      if (id == null) continue;
      const t = String(pick(m, 'title', 'Title') || '').trim();
      map.set(String(id), t || `Asset ${String(id).slice(0, 8)}`);
    }
    return map;
  }, [members]);

  useEffect(() => {
    if (!open) return;
    setPhase('review');
    setPreparedMint(null);
    setSignedMintInput('');
    setBulkMintLineItems([]);
    setBulkMintCursor(0);
    const first = members[0];
    const addr = first?.currentOwnerIdentifier ?? first?.CurrentOwnerIdentifier ?? first?.ownerAddress ?? first?.OwnerAddress ?? '';
    setOwnerWallet(String(addr || '').trim());
    setMemberAssetDetail(null);
    setBulkMemberMintHistory([]);
    setSigningBroadcastStarted(false);
    setOptimisticMintByAssetId({});
  }, [open, bulkId, assetClass, members]);

  const refreshBulkMintHistory = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!bulkId) return;
    try {
      if (!silent) setBulkMintHistoryLoading(true);
      const data = await listBulkMemberMintHistory(bulkId);
      setBulkMemberMintHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!silent) {
        toast.error(e.response?.data?.message || e.message || 'Could not load mint history');
      }
      setBulkMemberMintHistory([]);
    } finally {
      if (!silent) setBulkMintHistoryLoading(false);
    }
  }, [bulkId, listBulkMemberMintHistory]);

  useEffect(() => {
    if (!open || !bulkId) return;
    if (phase === 'broadcastSummary') {
      refreshBulkMintHistory({ silent: false });
      return;
    }
    if (phase !== 'signing' || !signingBroadcastStarted) return;
    refreshBulkMintHistory({ silent: true });
  }, [open, phase, bulkId, signingBroadcastStarted, refreshBulkMintHistory]);

  const historyByAssetId = useMemo(() => {
    const m = new Map();
    for (const e of bulkMemberMintHistory) {
      const id = e?.assetId ?? e?.AssetId;
      if (id == null) continue;
      m.set(String(id), e);
    }
    return m;
  }, [bulkMemberMintHistory]);

  const resolvedClass = assetClass;
  const mintArtifactTemplateId = useMemo(() => {
    if (!resolvedClass) return null;
    return resolveContractTemplateIdForAssetClass(resolvedClass);
  }, [resolvedClass]);

  const mintTemplateMeta = useMemo(() => {
    if (!mintArtifactTemplateId) return null;
    return getStandardContractTemplate(mintArtifactTemplateId);
  }, [mintArtifactTemplateId]);

  const resolveMintArtifact = useCallback(() => {
    if (!mintArtifactTemplateId) {
      throw new Error('Cannot resolve mint ABI: missing asset class.');
    }
    return resolveContractArtifact(mintArtifactTemplateId, {
      abiJson: '',
      bytecode: '',
      mintFunctionName: 'mint'
    });
  }, [mintArtifactTemplateId]);

  /** Full batch for offline tooling; UI still broadcasts one signed tx at a time in order. */
  const allUnsignedMintsBundle = useMemo(() => {
    if (!Array.isArray(bulkMintLineItems) || bulkMintLineItems.length === 0) return null;
    return {
      kind: 'PingBulkUnsignedERC721Mints',
      version: 1,
      itemCount: bulkMintLineItems.length,
      bulkRegistrationId: bulkId ?? null,
      instructions:
        'Each items[].unsignedMint is one ERC-721 mint. Sign in index order. This wizard submits one signed transaction per step; nonces in each payload must stay consistent with what is on-chain before broadcast.',
      items: bulkMintLineItems.map((row, idx) => ({
        index: idx + 1,
        assetId: row.assetId,
        unsignedMint: row.prepared
      }))
    };
  }, [bulkMintLineItems, bulkId]);

  const openMemberAssetDetail = useCallback(
    async (m) => {
      setMemberAssetDetail({ asset: m, loading: true });
      const id = pick(m, 'id', 'Id');
      if (!id) {
        setMemberAssetDetail({ asset: m, loading: false });
        return;
      }
      try {
        const full = await getAssetById(id, true);
        setMemberAssetDetail({ asset: full || m, loading: false });
      } catch {
        setMemberAssetDetail({ asset: m, loading: false });
        toast.error('Could not load full asset details; showing batch payload only.');
      }
    },
    [getAssetById]
  );

  const signerOutputTemplate = formatPreparedPayload(buildSignerOutputTemplate({ expectedContractAddress: null }));

  const pollForMintCompletion = useCallback(
    async (assetId) => {
      const std = normalizeAssetClassTokenStandard(resolvedClass?.tokenStandard);
      const tokenType =
        std === 'ERC1155' ? 'fractionalNFT' : std === 'ERC20' ? 'fungible' : 'nonFungible';
      const name = `Asset Token`;
      const symbol = 'ASSET';
      const cfg = { tokenType, name, symbol };

      for (let attempt = 0; attempt < 12; attempt += 1) {
        const mintedToken = await getMintedTokenByAssetId(assetId, cfg);
        if (mintedToken) return mintedToken;
        const refreshedAsset = await getAssetById(assetId, true);
        if (MINTED_ASSET_STATUSES.has(refreshedAsset?.status)) {
          return getMintedTokenByAssetId(assetId, cfg);
        }
        await delay(5000);
      }
      return null;
    },
    [getAssetById, getMintedTokenByAssetId, resolvedClass?.tokenStandard]
  );

  const handleRebroadcastMint = useCallback(
    async (mintRequestId) => {
      if (!mintRequestId) return;
      try {
        setBusy(true);
        await rebroadcastMintRequest(mintRequestId);
        toast.success('Re-broadcast submitted');
        await refreshBulkMintHistory({ silent: true });
        try {
          await loadAssets();
        } catch {
          /* ignore */
        }
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || 'Re-broadcast failed');
      } finally {
        setBusy(false);
      }
    },
    [rebroadcastMintRequest, refreshBulkMintHistory, loadAssets]
  );

  const handlePrepare = async () => {
    if (!bulkId || !resolvedClass?.contractAddress) {
      toast.error('Missing bulk registration or deployed class contract.');
      return;
    }
    if (!signerAddress?.trim() || !ownerWallet?.trim()) {
      toast.error('Signer address and recipient wallet are required.');
      return;
    }
    if (!mintArtifactTemplateId) {
      toast.error('Missing asset class; cannot resolve mint ABI for this batch.');
      return;
    }
    let artifact;
    try {
      artifact = resolveMintArtifact();
    } catch (e) {
      toast.error(e.message || 'Could not resolve mint contract ABI');
      return;
    }
    try {
      setBusy(true);
      const raw = await prepareBulkMintByRegistration(bulkId, {
        signerAddress: signerAddress.trim(),
        ownerWallet: ownerWallet.trim(),
        contractAddress: resolvedClass.contractAddress,
        contractAbi: artifact.abiJson,
        functionName: artifact.mintFunctionName,
        tokenStandard: 'ERC721'
      });
      const normalized = normalizeBulkMintPrepareResponse(raw);
      if (!normalized.items.length) {
        toast.error('Bulk prepare returned no line items.');
        return;
      }
      setBulkMintLineItems(normalized.items);
      setBulkMintCursor(0);
      setPreparedMint(normalized.items[0].prepared);
      setPhase('signing');
      toast.success(`Prepared ${normalized.items.length} unsigned mints. Sign and broadcast in order.`);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to prepare bulk mint');
    } finally {
      setBusy(false);
    }
  };

  const handleBroadcast = async () => {
    if (!preparedMint || !resolvedClass?.contractAddress) {
      toast.error('Nothing to broadcast.');
      return;
    }
    if (!bulkMintLineItems.length) {
      toast.error('Bulk mint state out of sync.');
      return;
    }
    let parsedSigned;
    try {
      parsedSigned = parseSignedTransactionInput(signedMintInput);
    } catch (e) {
      toast.error(e.message || 'Invalid signed transaction');
      return;
    }
    const signedEntries = Array.isArray(parsedSigned) ? parsedSigned : [parsedSigned];

    let artifact;
    try {
      artifact = resolveMintArtifact();
    } catch (e) {
      toast.error(e.message || 'Could not resolve mint contract ABI');
      return;
    }

    const std = normalizeAssetClassTokenStandard(resolvedClass?.tokenStandard);
    const tokenType =
      std === 'ERC1155' ? 'fractionalNFT' : std === 'ERC20' ? 'fungible' : 'nonFungible';
    const mintPollCfg = { tokenType, name: 'Asset Token', symbol: 'ASSET' };

    const broadcastOne = async (lineIndex, signedHex) => {
      const line = bulkMintLineItems[lineIndex];
      if (!line?.assetId) {
        throw new Error('Bulk mint state out of sync.');
      }
      const prepared = line.prepared;
      const resolvedId = pickPreparedResolvedTokenId(prepared);
      if (!resolvedId) {
        throw new Error(`Missing resolved token id for mint ${lineIndex + 1}.`);
      }
      const mintReq = await submitMintToken(line.assetId, {
        signedTransactionHex: signedHex,
        tokenId: String(resolvedId),
        ownerWallet: ownerWallet.trim(),
        contractAddress: resolvedClass.contractAddress,
        contractAbi: artifact.abiJson,
        functionName: artifact.mintFunctionName
      });
      const mintRowId = mintReq?.id ?? mintReq?.Id;
      if (mintRowId != null && line.assetId != null) {
        setOptimisticMintByAssetId((prev) => ({
          ...prev,
          [String(line.assetId)]: {
            id: mintRowId,
            status: mintReq?.status ?? mintReq?.Status,
            transactionHash: mintReq?.transactionHash ?? mintReq?.TransactionHash,
            failureReason: mintReq?.failureReason ?? mintReq?.FailureReason,
            tokenId: mintReq?.tokenId ?? mintReq?.TokenId
          }
        }));
      }
      const st = String(mintReq?.status ?? mintReq?.Status ?? '').toLowerCase();
      if (st === 'failed') {
        await refreshBulkMintHistory({ silent: true });
        throw new Error(mintReq.failureReason || mintReq.FailureReason || 'Mint failed on-chain.');
      }
      if (st === 'confirmed') {
        try {
          await getMintedTokenByAssetId(line.assetId, mintPollCfg);
        } catch {
          /* non-fatal */
        }
        await refreshBulkMintHistory({ silent: true });
        return;
      }
      if (st === 'submittedtoblockchain') {
        const mintedToken = await pollForMintCompletion(line.assetId);
        if (!mintedToken) {
          throw new Error('Mint submitted but confirmation not received yet.');
        }
        await refreshBulkMintHistory({ silent: true });
        return;
      }
      await refreshBulkMintHistory({ silent: true });
      throw new Error(
        `Unexpected mint status after broadcast: ${mintReq?.status ?? mintReq?.Status ?? 'unknown'}`
      );
    };

    const multiSignedFile = signedEntries.length > 1;
    let multiBroadcastFrom = 0;
    if (multiSignedFile) {
      if (signedEntries.length !== bulkMintLineItems.length) {
        toast.error(
          `Signed batch has ${signedEntries.length} transaction(s); this prepare step has ${bulkMintLineItems.length}. Re-prepare or use a matching signer file.`
        );
        return;
      }
      multiBroadcastFrom = bulkMintCursor;
      if (multiBroadcastFrom >= bulkMintLineItems.length) {
        toast.error('All mints in this batch are already past the last step.');
        return;
      }
      for (let i = multiBroadcastFrom; i < bulkMintLineItems.length; i++) {
        const hex = signedEntries[i]?.signedTransactionHex;
        if (!hex || typeof hex !== 'string') {
          toast.error(`Signed file entry ${i + 1} is missing signedTransactionHex.`);
          return;
        }
      }
    } else {
      const signedForStep = signedEntries[0];
      if (!signedForStep?.signedTransactionHex) {
        toast.error('Signed transaction JSON must contain signedTransactionHex.');
        return;
      }
    }

    const remainingCount = bulkMintLineItems.length - multiBroadcastFrom;
    const loadingToast = toast.loading(
      multiSignedFile
        ? remainingCount === bulkMintLineItems.length
          ? `Broadcasting all ${bulkMintLineItems.length} mints sequentially…`
          : `Broadcasting mints ${multiBroadcastFrom + 1}–${bulkMintLineItems.length}…`
        : 'Broadcasting…'
    );

    try {
      setBusy(true);
      setSigningBroadcastStarted(true);

      if (multiSignedFile) {
        for (let i = multiBroadcastFrom; i < bulkMintLineItems.length; i++) {
          setBulkMintCursor(i);
          setPreparedMint(bulkMintLineItems[i].prepared);
          await broadcastOne(i, String(signedEntries[i].signedTransactionHex).trim());
        }
        toast.dismiss(loadingToast);
        const broadcastCount = bulkMintLineItems.length - multiBroadcastFrom;
        toast.success(
          broadcastCount === bulkMintLineItems.length
            ? `All ${bulkMintLineItems.length} mints broadcast.`
            : `Successfully broadcast ${broadcastCount} mint(s) (steps ${multiBroadcastFrom + 1}–${bulkMintLineItems.length}).`
        );
        try {
          await loadAssets();
        } catch {
          /* ignore */
        }
        try {
          await refreshBulkMintHistory({ silent: true });
        } catch {
          /* ignore */
        }
        onCompleted?.();
        setPhase('broadcastSummary');
      } else {
        const signedForStep = signedEntries[0];
        await broadcastOne(bulkMintCursor, String(signedForStep.signedTransactionHex).trim());
        toast.dismiss(loadingToast);

        if (bulkMintCursor < bulkMintLineItems.length - 1) {
          const completedStep = bulkMintCursor + 1;
          const next = bulkMintCursor + 1;
          setBulkMintCursor(next);
          setPreparedMint(bulkMintLineItems[next].prepared);
          setSignedMintInput('');
          toast.success(
            `Mint ${completedStep} of ${bulkMintLineItems.length} done. Paste the next signed transaction, or upload the full multi-result signer file to broadcast the rest in one click.`
          );
          try {
            await loadAssets();
          } catch {
            /* ignore */
          }
        } else {
          toast.success('Batch mint complete.');
          try {
            await loadAssets();
          } catch {
            /* ignore */
          }
          try {
            await refreshBulkMintHistory({ silent: true });
          } catch {
            /* ignore */
          }
          onCompleted?.();
          setPhase('broadcastSummary');
        }
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error(e.response?.data?.message || e.message || 'Broadcast failed');
      try {
        await refreshBulkMintHistory({ silent: true });
      } catch {
        /* ignore */
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open || !bulkRegistration) return null;

  const title = pick(bulkRegistration, 'title', 'Title') || 'Bulk registration';
  const detailA = memberAssetDetail?.asset;

  const onSignedFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSignedMintInput(String(reader.result || '').trim());
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <WizardContainer>
              <WizardHeader>
                <WizardTitleBlock>
                  <WizardTitle>
                    <FiLayers aria-hidden />
                    Bulk tokenization
                  </WizardTitle>
                  <WizardSubtitle>
                    {title} — prepare unsigned mints, sign with your cold wallet or Ping signer, then broadcast.
                    ERC-721 only. If your signer file contains one result per mint, one <strong>Broadcast signed mint</strong>{' '}
                    click submits every transaction in order; a single signed entry still broadcasts one mint per click.
                  </WizardSubtitle>
                </WizardTitleBlock>
                <CloseButton onClick={onClose} aria-label="Close">
                  <FiX aria-hidden />
                </CloseButton>
              </WizardHeader>

              <ProgressBar>
                <ProgressStep $active={phase === 'review'} $completed={phase !== 'review'}>
                  <div className="step-number">1</div>
                  <div className="step-label">Review batch</div>
                </ProgressStep>
                <StepConnector $completed={phase !== 'review'} />
                <ProgressStep
                  $active={phase === 'configure'}
                  $completed={phase === 'signing' || phase === 'broadcastSummary'}
                >
                  <div className="step-number">2</div>
                  <div className="step-label">Configure &amp; prepare</div>
                </ProgressStep>
                <StepConnector $completed={phase === 'signing' || phase === 'broadcastSummary'} />
                <ProgressStep $active={phase === 'signing'} $completed={phase === 'broadcastSummary'}>
                  <div className="step-number">3</div>
                  <div className="step-label">Sign &amp; broadcast</div>
                </ProgressStep>
                <StepConnector $completed={phase === 'broadcastSummary'} />
                <ProgressStep $active={phase === 'broadcastSummary'} $completed={false}>
                  <div className="step-number">4</div>
                  <div className="step-label">Broadcast results</div>
                </ProgressStep>
              </ProgressBar>

              <AnimatePresence mode="wait">
                {phase === 'review' && (
                  <StepContent
                    key="review"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <StatusPanel>
                      <div className="title">Offline signing &amp; broadcast</div>
                      <div className="message">
                        Review members below, then configure signer addresses and prepare one unsigned transaction per
                        asset. Keys never leave your offline signer.
                      </div>
                      <FlowSteps>
                        <li>
                          <strong>Prepare</strong> — API returns unsigned mint JSON per line item.
                        </li>
                        <li>
                          <strong>Sign offline</strong> — Use the address you enter as the authorized tokenizer signer.
                        </li>
                        <li>
                          <strong>Broadcast</strong> — Upload the multi-result signer JSON (or paste one signed mint at a
                          time). Multiple <code>results[]</code> entries are broadcast automatically in order on one click.
                        </li>
                      </FlowSteps>
                    </StatusPanel>

                    <ReviewCard>
                      <div className="review-header">
                        <div className="review-icon">
                          <FiPackage aria-hidden />
                        </div>
                        <div>
                          <div className="review-title">Batch summary</div>
                        </div>
                      </div>
                      <div className="review-meta">
                        <strong style={{ color: '#fff' }}>{members.length}</strong> member asset(s)
                        <br />
                        Class: <strong style={{ color: '#fff' }}>{resolvedClass?.name || '—'}</strong>
                        <br />
                        Contract:{' '}
                        {resolvedClass?.contractAddress ? (
                          <code>{resolvedClass.contractAddress}</code>
                        ) : (
                          <span style={{ color: '#ff6b6b' }}>Not deployed</span>
                        )}
                      </div>
                      <MemberTableWrap>
                        <MemberTable>
                          <thead>
                            <tr>
                              <th>Asset #</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th style={{ width: 56, textAlign: 'center' }}>Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {membersSortedByTitle.map((m, i) => (
                              <tr key={pick(m, 'id', 'Id') || i}>
                                <td>{pick(m, 'assetNumber', 'AssetNumber') || '—'}</td>
                                <td>{pick(m, 'title', 'Title') || '—'}</td>
                                <td>{formatEnumLabel(String(pick(m, 'status', 'Status') ?? '—'))}</td>
                                <td style={{ textAlign: 'center' }}>
                                  <RowIconBtn
                                    type="button"
                                    title="View asset details"
                                    aria-label={`Details for asset ${pick(m, 'assetNumber', 'AssetNumber') || pick(m, 'id', 'Id') || i}`}
                                    onClick={() => openMemberAssetDetail(m)}
                                  >
                                    <FiEye aria-hidden />
                                  </RowIconBtn>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </MemberTable>
                      </MemberTableWrap>
                    </ReviewCard>

                    <WizardActions>
                      <span />
                      <ActionButton type="button" className="primary" onClick={() => setPhase('configure')}>
                        Next
                        <FiChevronRight aria-hidden />
                      </ActionButton>
                    </WizardActions>
                  </StepContent>
                )}

                {phase === 'configure' && (
                  <StepContent
                    key="configure"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FormSection>
                      <div className="section-title">
                        <FiShield aria-hidden />
                        Custody &amp; compliance
                      </div>
                      <FormGroup>
                        <label htmlFor="bw-signer">Offline signer address *</label>
                        <input
                          id="bw-signer"
                          value={signerAddress}
                          onChange={(e) => setSignerAddress(e.target.value)}
                          placeholder="0xSignerAddress"
                          autoComplete="off"
                        />
                        <div className="helper">Must match the address used by your offline signer app.</div>
                      </FormGroup>
                      <FormGroup>
                        <label htmlFor="bw-owner">Recipient wallet (token owner) *</label>
                        <input
                          id="bw-owner"
                          value={ownerWallet}
                          onChange={(e) => setOwnerWallet(e.target.value)}
                          placeholder="0xRecipientWallet"
                          autoComplete="off"
                        />
                        <div className="helper">NFTs will be minted to this wallet.</div>
                      </FormGroup>
                    </FormSection>

                    <FormSection>
                      <div className="section-title">
                        <FiLayers aria-hidden />
                        Deployed collection contract
                      </div>
                      <p className="helper" style={{ margin: '0 0 0.75rem', lineHeight: 1.55 }}>
                        Like the Assets Registry tokenization flow, mint encoding uses the catalog ABI inferred from this
                        asset class (standard + category). It always targets the class contract already deployed below — no
                        separate template selection.
                      </p>
                      {resolvedClass?.contractAddress ? (
                        <ContractAddressRow>
                          <ContractAddressMono>{resolvedClass.contractAddress}</ContractAddressMono>
                          <ContractExplorerIconBtn
                            type="button"
                            title={
                              explorerConfigured
                                ? 'View contract in block explorer'
                                : 'Block explorer not configured'
                            }
                            aria-label="View contract in block explorer"
                            disabled={!explorerConfigured}
                            onClick={() => {
                              if (!explorerConfigured) return;
                              const url = addressExplorerUrl(resolvedClass.contractAddress);
                              if (url) window.open(url, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <FiExternalLink aria-hidden />
                          </ContractExplorerIconBtn>
                        </ContractAddressRow>
                      ) : null}
                      {mintTemplateMeta ? (
                        <p className="helper" style={{ margin: 0, lineHeight: 1.5 }}>
                          <strong style={{ color: '#cbd5e1' }}>
                            {mintTemplateMeta.name} ({mintTemplateMeta.versionLabel})
                          </strong>
                          {mintTemplateMeta.summary ? (
                            <>
                              <br />
                              {mintTemplateMeta.summary}
                            </>
                          ) : null}
                        </p>
                      ) : null}
                    </FormSection>

                    <WizardActions>
                      <ActionButton type="button" className="secondary" onClick={() => setPhase('review')}>
                        <FiChevronLeft aria-hidden />
                        Previous
                      </ActionButton>
                      <ActionButton type="button" className="primary" onClick={handlePrepare} disabled={busy}>
                        <FiCpu aria-hidden />
                        {busy ? 'Preparing…' : 'Prepare unsigned mints'}
                      </ActionButton>
                    </WizardActions>
                  </StepContent>
                )}

                {phase === 'signing' && (
                  <StepContent
                    key="signing"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SignStepBanner>
                      Mint <strong>{bulkMintCursor + 1}</strong> of <strong>{bulkMintLineItems.length}</strong> — sign and
                      broadcast the highlighted card’s payload next. Use <strong>Copy all mints</strong> /{' '}
                      <strong>Download all mints</strong> for one JSON file, or download each asset below.
                    </SignStepBanner>

                    <PayloadBox>
                      <div className="title">Unsigned mint payloads</div>
                      <div className="hint">
                        Sign offline; do not paste private keys in the browser. <strong>Copy all mints</strong> /{' '}
                        <strong>Download all mints</strong> exports the full batch. Each card lists the resolved token ID
                        and a per-asset JSON download.
                      </div>
                      {allUnsignedMintsBundle ? (
                        <PayloadActions>
                          <MiniButton
                            onClick={() => {
                              copyTextToClipboard(formatPreparedPayload(allUnsignedMintsBundle));
                              toast.success(`Copied all ${bulkMintLineItems.length} unsigned mints`);
                            }}
                          >
                            <FiLayers aria-hidden />
                            Copy all mints
                          </MiniButton>
                          <MiniButton
                            onClick={() => {
                              const slug = String(bulkId || 'batch').replace(/[^a-zA-Z0-9-]/g, '').slice(0, 12);
                              downloadJsonFile(`bulk-mint-${slug || 'batch'}-all-unsigned.json`, allUnsignedMintsBundle);
                              toast.success('Downloaded full batch');
                            }}
                          >
                            <FiDownload aria-hidden />
                            Download all mints
                          </MiniButton>
                        </PayloadActions>
                      ) : null}
                      <UnsignedMintGrid>
                        {bulkMintLineItems.map((row, idx) => {
                          const aid = row.assetId;
                          const prepared = row.prepared;
                          const tokenId = pickPreparedResolvedTokenId(prepared);
                          const title =
                            (aid != null && memberTitleByAssetId.get(String(aid))) ||
                            (aid != null ? `Asset ${String(aid).slice(0, 8)}` : 'Asset');
                          const tokenLabel = tokenId || '—';
                          const fileSlug = slugForMintFilename(aid);
                          const tokSlug = slugForMintFilename(tokenId || idx);
                          const fname = `bulk-mint-token-${tokSlug || idx}-asset-${fileSlug || idx}.json`;
                          return (
                            <UnsignedMintCard
                              key={`${String(aid ?? 'row')}-${idx}`}
                              className={idx === bulkMintCursor ? 'current' : ''}
                            >
                              <UnsignedMintCardMeta>
                                <UnsignedMintStepPill>
                                  Step {idx + 1} / {bulkMintLineItems.length}
                                </UnsignedMintStepPill>
                                {idx === bulkMintCursor ? (
                                  <UnsignedMintCurrentBadge>Broadcast this next</UnsignedMintCurrentBadge>
                                ) : null}
                              </UnsignedMintCardMeta>
                              <UnsignedMintAssetTitle title={title}>{title}</UnsignedMintAssetTitle>
                              <UnsignedMintTokenRow>
                                <UnsignedMintTokenLabel>Token ID</UnsignedMintTokenLabel>
                                <UnsignedMintTokenId>{tokenLabel}</UnsignedMintTokenId>
                              </UnsignedMintTokenRow>
                              <MiniButton
                                onClick={() => {
                                  downloadJsonFile(fname, prepared);
                                  toast.success('Downloaded unsigned mint JSON');
                                }}
                              >
                                <FiDownload aria-hidden />
                                Download JSON
                              </MiniButton>
                            </UnsignedMintCard>
                          );
                        })}
                      </UnsignedMintGrid>
                    </PayloadBox>

                    <PayloadBox>
                      <div className="title">Signer output template</div>
                      <div className="hint">Use one signer output object per mint. Important field: signedTransactionHex.</div>
                      <PayloadActions>
                        <MiniButton
                          onClick={() => {
                            copyTextToClipboard(signerOutputTemplate);
                            toast.success('Copied signer template');
                          }}
                        >
                          <FiCopy aria-hidden />
                          Copy template
                        </MiniButton>
                      </PayloadActions>
                      <textarea readOnly value={signerOutputTemplate} />
                    </PayloadBox>

                    <PayloadBox>
                      <div className="title">Signed mint — upload or paste</div>
                      <div className="hint">
                        Upload the signer output file (including <code>batchSignedTransactions</code> +{' '}
                        <code>results[]</code>) or paste one mint’s JSON / raw <code>signedTransactionHex</code>. If{' '}
                        <code>results[]</code> has one entry per prepared mint, a single broadcast sends every mint in
                        sequence; otherwise each click submits one mint.
                      </div>
                      <PayloadActions>
                        <FilePickLabel>
                          <FiUpload aria-hidden />
                          Upload signed file
                          <input
                            type="file"
                            accept=".json,.txt,text/json,application/json"
                            onChange={onSignedFile}
                          />
                        </FilePickLabel>
                      </PayloadActions>
                      <textarea
                        value={signedMintInput}
                        onChange={(e) => setSignedMintInput(e.target.value)}
                        placeholder="Paste signedTransactionHex or full signer JSON here…"
                      />
                    </PayloadBox>

                    {signingBroadcastStarted ? (
                      <PayloadBox>
                        <div className="title">On-chain mint attempts (saved on server)</div>
                        <div className="hint">
                          Rows update as each transaction is submitted. Status and hashes sync from the server in the
                          background; use <strong>Explorer</strong> to open receipts and <strong>Re-broadcast</strong> if a
                          send fails.
                        </div>
                        {bulkMintHistoryLoading ? (
                          <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                            Syncing mint history…
                          </p>
                        ) : null}
                        <MemberTableWrap>
                          <MemberTable>
                            <thead>
                              <tr>
                                <th>Step</th>
                                <th>Asset</th>
                                <th>Latest status</th>
                                <th>Transaction</th>
                                <th style={{ minWidth: 120 }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkMintLineItems.map((row, idx) => {
                                const aid = row.assetId;
                                const title =
                                  (aid != null && memberTitleByAssetId.get(String(aid))) ||
                                  (aid != null ? `Asset ${String(aid).slice(0, 8)}` : 'Asset');
                                const entry = aid != null ? historyByAssetId.get(String(aid)) : null;
                                const serverLatest = entry ? latestMintRequestFromMemberHistory(entry) : null;
                                const opt = aid != null ? optimisticMintByAssetId[String(aid)] : null;
                                const latest = serverLatest ?? opt ?? null;
                                const statusRaw = latest?.status ?? latest?.Status ?? '';
                                const tx = latest?.transactionHash ?? latest?.TransactionHash ?? '';
                                const mid = latest?.id ?? latest?.Id;
                                const canRebroadcast = statusRaw && mintRequestAllowsRebroadcast(statusRaw);
                                return (
                                  <tr key={`mint-hist-${String(aid ?? idx)}`}>
                                    <td>{idx + 1}</td>
                                    <td>{title}</td>
                                    <td>
                                      {latest ? (
                                        <span style={{ color: statusRaw === 'failed' ? '#ff6b6b' : '#e2e8f0' }}>
                                          {formatMintRequestStatus(statusRaw)}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#64748b' }}>No attempt yet</span>
                                      )}
                                      {latest && (latest.failureReason || latest.FailureReason) ? (
                                        <div
                                          style={{
                                            marginTop: '0.25rem',
                                            fontSize: '0.75rem',
                                            color: '#94a3b8',
                                            maxWidth: '14rem',
                                            lineHeight: 1.35
                                          }}
                                          title={String(latest.failureReason || latest.FailureReason)}
                                        >
                                          {String(latest.failureReason || latest.FailureReason)}
                                        </div>
                                      ) : null}
                                    </td>
                                    <td>
                                      <code style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>
                                        {shortenTxHash(tx)}
                                      </code>
                                    </td>
                                    <td>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                        <MiniButton
                                          type="button"
                                          disabled={!explorerConfigured || !String(tx).trim()}
                                          title={
                                            explorerConfigured && String(tx).trim()
                                              ? 'View transaction in block explorer'
                                              : 'Explorer not configured or no hash yet'
                                          }
                                          onClick={() => {
                                            const url = transactionExplorerUrl(String(tx).trim());
                                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                          }}
                                        >
                                          <FiExternalLink aria-hidden />
                                          Explorer
                                        </MiniButton>
                                        {canRebroadcast && mid ? (
                                          <MiniButton
                                            type="button"
                                            disabled={busy}
                                            title="Re-send the stored signed raw transaction"
                                            onClick={() => handleRebroadcastMint(mid)}
                                          >
                                            Re-broadcast
                                          </MiniButton>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </MemberTable>
                        </MemberTableWrap>
                      </PayloadBox>
                    ) : null}

                    <WizardActions>
                      <ActionButton
                        type="button"
                        className="secondary"
                        onClick={() => {
                          setBulkMintLineItems([]);
                          setPreparedMint(null);
                          setSignedMintInput('');
                          setBulkMintCursor(0);
                          setSigningBroadcastStarted(false);
                          setBulkMemberMintHistory([]);
                          setOptimisticMintByAssetId({});
                          setPhase('configure');
                        }}
                        disabled={busy}
                      >
                        <FiChevronLeft aria-hidden />
                        Previous
                      </ActionButton>
                      <ActionButton
                        type="button"
                        className="primary"
                        onClick={handleBroadcast}
                        disabled={busy || !signedMintInput.trim()}
                      >
                        <FiCpu aria-hidden />
                        {busy ? 'Broadcasting…' : 'Broadcast signed mint'}
                      </ActionButton>
                    </WizardActions>
                  </StepContent>
                )}

                {phase === 'broadcastSummary' && (
                  <StepContent
                    key="broadcastSummary"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <StatusPanel style={{ borderColor: 'rgba(0, 255, 136, 0.25)', background: 'rgba(0, 255, 136, 0.06)' }}>
                      <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#86efac' }}>
                        <FiCheckCircle aria-hidden />
                        Broadcast complete
                      </div>
                      <div className="message">
                        Every mint in this batch was submitted. Review status, token IDs, and transaction hashes below. Use
                        the explorer button to open each transaction on the chain. Close when you are done.
                      </div>
                    </StatusPanel>

                    {bulkMintHistoryLoading ? (
                      <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                        Refreshing on-chain details…
                      </p>
                    ) : null}

                    <PayloadBox style={{ marginTop: '1rem' }}>
                      <div className="title">Mint results</div>
                      <div className="hint">
                        Data is loaded from the server (same as Bulk assets registry). Failed mints can be re-broadcast from
                        here if needed.
                      </div>
                      <MemberTableWrap>
                        <MemberTable>
                          <thead>
                            <tr>
                              <th>Step</th>
                              <th>Asset</th>
                              <th>Status</th>
                              <th>Token ID</th>
                              <th>Mint transaction</th>
                              <th style={{ textAlign: 'center', width: 56 }}>Chain</th>
                              <th style={{ minWidth: 120 }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkMintLineItems.map((row, idx) => {
                              const aid = row.assetId;
                              const title =
                                (aid != null && memberTitleByAssetId.get(String(aid))) ||
                                (aid != null ? `Asset ${String(aid).slice(0, 8)}` : 'Asset');
                              const entry = aid != null ? historyByAssetId.get(String(aid)) : null;
                              const mints = entry?.mintRequests ?? entry?.MintRequests ?? [];
                              const rowMint = preferredMintRequestForDisplay(mints);
                              const statusRaw = rowMint?.status ?? rowMint?.Status ?? '';
                              const txRaw = String(rowMint?.transactionHash ?? rowMint?.TransactionHash ?? '').trim();
                              const txShort = txRaw ? shortenTxHash(txRaw) : '';
                              const tokenId = String(rowMint?.tokenId ?? rowMint?.TokenId ?? '').trim();
                              const mid = rowMint?.id ?? rowMint?.Id;
                              const canRebroadcast = statusRaw && mintRequestAllowsRebroadcast(statusRaw);
                              const canOpenTx = explorerConfigured && Boolean(txRaw);
                              return (
                                <tr key={`summary-${String(aid ?? idx)}`}>
                                  <td>{idx + 1}</td>
                                  <td>{title}</td>
                                  <td>
                                    {rowMint ? (
                                      <span style={{ color: String(statusRaw).toLowerCase() === 'failed' ? '#ff6b6b' : '#e2e8f0' }}>
                                        {formatMintRequestStatus(statusRaw)}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#64748b' }}>—</span>
                                    )}
                                    {rowMint && (rowMint.failureReason || rowMint.FailureReason) ? (
                                      <div
                                        style={{
                                          marginTop: '0.25rem',
                                          fontSize: '0.75rem',
                                          color: '#94a3b8',
                                          maxWidth: '12rem',
                                          lineHeight: 1.35
                                        }}
                                        title={String(rowMint.failureReason || rowMint.FailureReason)}
                                      >
                                        {String(rowMint.failureReason || rowMint.FailureReason)}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    {tokenId ? (
                                      <code style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>{tokenId}</code>
                                    ) : (
                                      <span style={{ color: '#64748b' }}>—</span>
                                    )}
                                  </td>
                                  <td>
                                    {txShort ? (
                                      <code style={{ color: '#cbd5e1', fontSize: '0.78rem' }} title={txRaw}>
                                        {txShort}
                                      </code>
                                    ) : (
                                      <span style={{ color: '#64748b' }}>—</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <MiniButton
                                      type="button"
                                      disabled={!canOpenTx}
                                      title={
                                        canOpenTx
                                          ? 'View mint transaction in block explorer'
                                          : explorerConfigured
                                            ? 'No transaction hash'
                                            : 'Block explorer not configured'
                                      }
                                      aria-label="View mint transaction in block explorer"
                                      onClick={() => {
                                        if (!canOpenTx) return;
                                        const url = transactionExplorerUrl(txRaw);
                                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                      }}
                                      style={{ padding: '0.45rem 0.55rem', minWidth: 'auto' }}
                                    >
                                      <FiExternalLink aria-hidden />
                                    </MiniButton>
                                  </td>
                                  <td>
                                    {canRebroadcast && mid ? (
                                      <MiniButton
                                        type="button"
                                        disabled={busy}
                                        title="Re-send the stored signed raw transaction"
                                        onClick={() => handleRebroadcastMint(mid)}
                                      >
                                        Re-broadcast
                                      </MiniButton>
                                    ) : (
                                      <span style={{ color: '#64748b', fontSize: '0.8rem' }}>—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </MemberTable>
                      </MemberTableWrap>
                    </PayloadBox>

                    <WizardActions>
                      <span />
                      <ActionButton type="button" className="primary" onClick={onClose}>
                        <FiCheckCircle aria-hidden />
                        Done
                      </ActionButton>
                    </WizardActions>
                  </StepContent>
                )}
              </AnimatePresence>
            </WizardContainer>
          </ModalContent>
        </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && memberAssetDetail && (
          <AssetDetailOverlay
            key="bulk-member-detail"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMemberAssetDetail(null)}
          >
            <AssetDetailCard
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <AssetDetailHead>
                <AssetDetailHeadText>
                  <AssetDetailTitle>
                    <FiEye aria-hidden />
                    Asset details
                  </AssetDetailTitle>
                  {detailA ? (
                    <AssetDetailHeadSub>
                      {pick(detailA, 'assetNumber', 'AssetNumber') || 'No asset #'} ·{' '}
                      {String(pick(detailA, 'id', 'Id') || '').slice(0, 8)}
                      {pick(detailA, 'id', 'Id') ? '…' : ''}
                    </AssetDetailHeadSub>
                  ) : null}
                </AssetDetailHeadText>
                <CloseButton type="button" onClick={() => setMemberAssetDetail(null)} aria-label="Close details">
                  <FiX aria-hidden />
                </CloseButton>
              </AssetDetailHead>
              <AssetDetailBody>
                {detailA ? (
                  <AssetDetailFields asset={detailA} loading={memberAssetDetail.loading} />
                ) : null}
              </AssetDetailBody>
            </AssetDetailCard>
          </AssetDetailOverlay>
        )}
      </AnimatePresence>
    </>
  );
}

export default BulkTokenizeWizardModal;
